import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Download } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/common/States';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { financeReportsApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { UUID } from '@/types/domain';
import { formatDate, formatMoney, formatRate, orDash } from '@/utils/format';
import { Money } from './shared';
import { StudentPicker } from './StudentPicker';

type Report = 'collection' | 'outstanding' | 'summary' | 'ledger' | 'relief';

const TABS: { id: Report; label: string; blurb: string }[] = [
  { id: 'collection', label: 'Collection', blurb: 'What came in, from whom, and how.' },
  { id: 'outstanding', label: 'Outstanding', blurb: 'What is owed, oldest first, bucketed by age.' },
  { id: 'summary', label: 'Summary', blurb: 'Billed against collected, by class or by fee.' },
  { id: 'ledger', label: 'Student ledger', blurb: 'Every transaction for one child, in order.' },
  { id: 'relief', label: 'Discounts & awards', blurb: 'Relief granted, and who authorised it.' },
];

const startOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

/**
 * The five reports a finance office runs.
 *
 * <p>Each exports as CSV, generated by the backend from the same filters as the
 * table on screen. That is what keeps an export honest: it cannot be wider than
 * what the user is permitted to see, because the same permission and the same
 * query produce both.
 */
export function FinanceReportsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const context = useWorkingContext();

  const [tab, setTab] = useState<Report>('collection');
  const [range, setRange] = useState({ from: startOfMonth(), to: today() });
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [groupBy, setGroupBy] = useState<'CLASS' | 'CATEGORY'>('CLASS');
  const [student, setStudent] = useState<{ id: UUID; name: string; number?: string } | null>(null);
  const [downloading, setDownloading] = useState(false);

  const yearParams = context.academicYearId !== null
    ? { academicYearId: context.academicYearId }
    : {};

  const collection = useQuery({
    queryKey: queryKeys.finance.report('collection', range),
    queryFn: () => financeReportsApi.collection(range),
    enabled: tab === 'collection',
  });

  const outstanding = useQuery({
    queryKey: queryKeys.finance.report('outstanding', { ...yearParams, overdueOnly }),
    queryFn: () => financeReportsApi.outstanding({ ...yearParams, overdueOnly }),
    enabled: tab === 'outstanding',
  });

  const summary = useQuery({
    queryKey: queryKeys.finance.report('summary', { ...yearParams, groupBy }),
    queryFn: () => financeReportsApi.summary({ ...yearParams, groupBy }),
    enabled: tab === 'summary',
  });

  const ledger = useQuery({
    queryKey: queryKeys.finance.report('ledger', student?.id),
    queryFn: () => financeReportsApi.ledger(student?.id as UUID),
    enabled: tab === 'ledger' && student !== null,
  });

  const relief = useQuery({
    queryKey: queryKeys.finance.report('relief', yearParams),
    queryFn: () => financeReportsApi.relief(yearParams),
    enabled: tab === 'relief',
  });

  async function download() {
    setDownloading(true);
    try {
      const [path, params, name] =
        tab === 'collection' ? ['collection', range, 'collection-report.csv'] as const
        : tab === 'outstanding' ? ['outstanding', { ...yearParams, overdueOnly }, 'outstanding-report.csv'] as const
        : tab === 'summary' ? ['summary', { ...yearParams, groupBy }, 'fee-summary.csv'] as const
        : tab === 'relief' ? ['relief', yearParams, 'relief-report.csv'] as const
        : [`ledger/${student?.id}`, {}, 'student-ledger.csv'] as const;

      await financeReportsApi.download(path, params, name);
      toast.success('Download started');
    } catch (error) {
      handleApiError(error);
    } finally {
      setDownloading(false);
    }
  }

  const active = TABS.find((t) => t.id === tab);
  const canDownload = can(PERMISSIONS.FINANCE_REPORT_EXPORT) && (tab !== 'ledger' || student !== null);

  return (
    <div>
      <PageHeader
        title="Financial Reports"
        description={active?.blurb}
        icon={<ClipboardList className="h-5 w-5" aria-hidden="true" />}
        action={
          canDownload ? (
            <Button variant="secondary" loading={downloading} onClick={() => void download()}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Export CSV
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap gap-1 border-b-2 border-line" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={`-mb-0.5 border-b-2 px-3.5 py-2 text-[13px] transition-colors ${
              tab === entry.id
                ? 'border-accent font-semibold text-ink'
                : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3 px-4 py-3">
          {tab === 'collection' && (
            <>
              <TextField label="From" type="date" value={range.from}
                         onChange={(event) => setRange((r) => ({ ...r, from: event.target.value }))} />
              <TextField label="To" type="date" value={range.to}
                         onChange={(event) => setRange((r) => ({ ...r, to: event.target.value }))} />
            </>
          )}
          {tab === 'outstanding' && (
            <label className="flex items-center gap-2 text-sm text-muted">
              <input type="checkbox" checked={overdueOnly} className="h-4 w-4 border-line"
                     onChange={(event) => setOverdueOnly(event.target.checked)} />
              Past the due date only
            </label>
          )}
          {tab === 'summary' && (
            <div className="flex gap-1">
              {(['CLASS', 'CATEGORY'] as const).map((option) => (
                <button key={option} type="button" onClick={() => setGroupBy(option)}
                        className={`border px-3 py-1.5 text-[13px] transition-colors ${
                          groupBy === option ? 'border-accent bg-accent-soft text-accent-soft-fg'
                                             : 'border-line bg-surface text-muted hover:border-line-strong'}`}>
                  By {option.toLowerCase()}
                </button>
              ))}
            </div>
          )}
          {tab === 'ledger' && (
            <div className="min-w-[320px] flex-1">
              <StudentPicker value={student} onChange={setStudent} />
            </div>
          )}
          {tab === 'relief' && (
            <p className="text-[13px] text-muted">
              Every discount and award standing against an invoice in the working year.
            </p>
          )}
        </div>
      </Card>

      {tab === 'collection' && (
        <ReportTable
          query={collection}
          headers={['Date', 'Receipt', 'Student', 'Payer', 'Method', 'Received', 'Applied', 'Held', 'Status']}
          numeric={[5, 6, 7]}
          empty="No payments in this range"
          rows={(collection.data ?? []).map((row) => [
            formatDate(row.paymentDate),
            <span key="r" className="font-mono text-xs">{row.receiptNumber}</span>,
            orDash(row.studentName),
            orDash(row.payerName),
            row.paymentMethod.replaceAll('_', ' ').toLowerCase(),
            <Money key="a" amount={row.amount} bold />,
            <Money key="al" amount={row.allocatedAmount} />,
            <Money key="h" amount={row.heldOnAccount} />,
            row.status.replaceAll('_', ' ').toLowerCase(),
          ])}
        />
      )}

      {tab === 'outstanding' && (
        <ReportTable
          query={outstanding}
          headers={['Student', 'Class', 'Invoice', 'Due', 'Total', 'Paid', 'Outstanding', 'Age']}
          numeric={[4, 5, 6]}
          empty="Nothing outstanding"
          rows={(outstanding.data ?? []).map((row) => [
            orDash(row.studentName),
            orDash(row.gradeName),
            <span key="i" className="font-mono text-xs">{row.invoiceNumber}</span>,
            formatDate(row.dueDate),
            <Money key="t" amount={row.totalAmount} />,
            <Money key="p" amount={row.amountPaid} />,
            <Money key="o" amount={row.outstanding} bold />,
            <Badge key="b" tone={row.daysOverdue > 90 ? 'danger' : row.daysOverdue > 0 ? 'warning' : 'neutral'}>
              {row.ageBucket}
            </Badge>,
          ])}
        />
      )}

      {tab === 'summary' && (
        <ReportTable
          query={summary}
          headers={groupBy === 'CLASS'
            ? ['Class', 'Campus', 'Billed', 'Collected', 'Outstanding', 'Rate', 'Students']
            : ['Fee category', 'Billed', 'Collected']}
          numeric={groupBy === 'CLASS' ? [2, 3, 4, 5, 6] : [1]}
          empty="Nothing billed in this year"
          note={groupBy === 'CATEGORY'
            ? 'Collection is not attributed per category: a payment settles an invoice, not a line, so splitting it across the categories on that invoice would be a guess.'
            : undefined}
          rows={(summary.data ?? []).map((row) =>
            groupBy === 'CLASS'
              ? [
                  row.label,
                  orDash(row.campusName),
                  <Money key="b" amount={row.billed} />,
                  <Money key="c" amount={row.collected} />,
                  <Money key="o" amount={row.outstanding} bold />,
                  <span key="r" className="block text-right tabular-nums">{formatRate(row.collectionRate)}</span>,
                  <span key="s" className="block text-right tabular-nums">{row.studentCount}</span>,
                ]
              : [
                  row.label,
                  <Money key="b" amount={row.billed} />,
                  <span key="c" className="text-subtle">not attributed</span>,
                ],
          )}
        />
      )}

      {tab === 'ledger' && (
        student === null ? (
          <Card><EmptyState title="Choose a student"
                            description="A ledger is one child's complete financial history." /></Card>
        ) : (
          <ReportTable
            query={ledger}
            headers={['Date', 'Entry', 'Description', 'Debit', 'Credit', 'Balance']}
            numeric={[3, 4, 5]}
            empty="No entries yet"
            note="In the order entries were recorded, which is the order the running balance follows. A back-dated entry continues from the balance as it stood when it was written."
            rows={(ledger.data ?? []).map((row) => [
              formatDate(row.entryDate),
              row.entryType.replaceAll('_', ' ').toLowerCase(),
              row.description,
              <Money key="d" amount={row.debit} />,
              <Money key="c" amount={row.credit} />,
              <span key="b" className="block text-right font-semibold tabular-nums">
                {formatMoney(row.balance, '').trim()}
              </span>,
            ])}
          />
        )
      )}

      {tab === 'relief' && (
        <ReportTable
          query={relief}
          headers={['Type', 'Student', 'Invoice', 'Relief', 'Amount', 'Reason', 'Approval']}
          numeric={[4]}
          empty="No relief granted"
          rows={(relief.data ?? []).map((row) => [
            row.type.toLowerCase(),
            orDash(row.studentName),
            <span key="i" className="font-mono text-xs">{row.invoiceNumber}</span>,
            row.reliefName,
            <Money key="a" amount={row.amount} bold />,
            row.reason,
            orDash(row.approvalStatus?.toLowerCase()),
          ])}
        />
      )}
    </div>
  );
}

/** One shape for every report: they are all read as tables and exported as tables. */
function ReportTable({
  query,
  headers,
  rows,
  numeric,
  empty,
  note,
}: {
  query: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  headers: string[];
  rows: React.ReactNode[][];
  numeric: number[];
  empty: string;
  note?: string;
}) {
  if (query.isError) {
    return <Card><ErrorState error={query.error} onRetry={() => void query.refetch()} /></Card>;
  }

  return (
    <Card className="overflow-hidden">
      {note !== undefined && (
        <p className="border-b border-line bg-surface-2 px-4 py-2.5 text-xs text-muted">{note}</p>
      )}
      {query.isPending ? (
        <TableSkeleton columns={Math.min(headers.length, 6)} />
      ) : rows.length === 0 ? (
        <EmptyState title={empty} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2">
                  {headers.map((header, index) => (
                    <th key={header} scope="col"
                        className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-subtle ${
                          numeric.includes(index) ? 'text-right' : 'text-left'}`}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}
                          className={`px-4 py-2.5 ${numeric.includes(cellIndex) ? '' : 'text-ink'}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-line px-4 py-2 text-[11.5px] text-muted">
            {rows.length} row{rows.length === 1 ? '' : 's'}
          </p>
        </>
      )}
    </Card>
  );
}
